// CustomToolbarV2.tsx - MUI X Data Grid v8 (no deprecated APIs)
import * as React from "react";
import { useContext } from "react";

import { CustomExportButton, type PdfMeta } from "./CustomExportButton";
import FilterAltRoundedIcon from "@mui/icons-material/FilterAltRounded";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import ViewWeekRoundedIcon from "@mui/icons-material/ViewWeekRounded";
import {
	Box,
	IconButton,
	InputAdornment,
	type Theme,
	Tooltip,
	alpha,
} from "@mui/material";
import {
	ColumnsPanelTrigger,
	FilterPanelTrigger,
	GridToolbarQuickFilter,
	Toolbar,
	ToolbarButton,
} from "@mui/x-data-grid";
import { Language } from "@/model/Language/Language";

import { TranslationContext } from "@/app/providers";

import { GetLanguageValueAsString } from "@/app/application/Translate";


export type { PdfMeta };

type Props = {
	rows: unknown[];
	columns: unknown[];
	fileName?: string;
	pdfMeta?: PdfMeta;
	quickFilterProps?: { debounceMs?: number };
	hideQuickFilter?: boolean;
	showPdfOptions?: boolean;
	hidden?: boolean;
};

const getIconBtnSx = () => ({
	borderRadius: 2,
	border: "1px solid",
	borderColor: "divider",
	bgcolor: "background.paper",
	boxShadow: "none",
	transition: "background-color 140ms ease, border-color 140ms ease",
	"&:hover": {
		bgcolor: "action.hover",
		borderColor: "text.disabled",
	},
	"&:focus-visible": {
		outline: "2px solid",
		outlineColor: "primary.main",
		outlineOffset: "2px",
	},
	width: 32,
	height: 32,
});

const panelSx = {
	"& ~ .MuiDataGrid-panel": {
		top: "calc(var(--DataGrid-headerHeight, 56px) + 10px) !important",
	},
	"& ~ .MuiDataGrid-panel .MuiPaper-root": {
		borderRadius: 12,
		border: "1px solid",
		borderColor: "divider",
		bgcolor: "background.paper",
		color: "text.primary",
		boxShadow: (t: Theme) =>
			t.palette.mode === "dark"
				? `0 20px 48px ${alpha(t.palette.common.black, 0.42)}`
				: `0 12px 28px ${alpha(t.palette.common.black, 0.12)}`,
		overflow: "hidden",
	},
	"& ~ .MuiDataGrid-panel .MuiDataGrid-panelHeader": {
		bgcolor: "background.paper",
		borderBottom: "1px solid",
		borderColor: "divider",
	},
	"& ~ .MuiDataGrid-panel .MuiOutlinedInput-root": {
		borderRadius: 10,
		bgcolor: "background.paper",
		"& .MuiOutlinedInput-notchedOutline": {
			borderColor: "divider",
		},
		"&:hover .MuiOutlinedInput-notchedOutline": {
			borderColor: "text.disabled",
		},
		"&.Mui-focused": {
			boxShadow: (t: Theme) =>
				`0 0 0 3px ${alpha(t.palette.primary.main, t.palette.mode === "dark" ? 0.24 : 0.16)}`,
		},
		"& .MuiOutlinedInput-input": {
			color: "text.primary",
			fontWeight: 500,
		},
		"& .MuiInputAdornment-root": { color: "text.secondary" },
	},
	"& ~ .MuiDataGrid-panel .MuiTypography-root": {
		color: "text.primary",
	},
	"& ~ .MuiDataGrid-panel .MuiCheckbox-root": {
		color: "text.secondary",
		"&.Mui-checked": { color: "primary.main" },
	},
	"& ~ .MuiDataGrid-panel .MuiDataGrid-columnsPanelRow:hover": {
		bgcolor: "action.hover",
	},
	"& ~ .MuiDataGrid-panel .MuiDataGrid-panelFooter": {
		bgcolor: "background.default",
		borderTop: "1px solid",
		borderColor: "divider",
	},
	"& ~ .MuiDataGrid-panel .MuiDataGrid-columnsPanelRowLabel": {
		color: "text.primary",
	},
	"& ~ .MuiDataGrid-panel .MuiDataGrid-columnsPanelRow .MuiTypography-root": {
		color: "text.primary",
	},
	"& ~ .MuiDataGrid-panel .MuiDataGrid-columnsPanelRow label, & ~ .MuiDataGrid-panel .MuiDataGrid-columnsPanelRow span":
		{
			color: "text.primary",
		},
	"& ~ .MuiDataGrid-panel .MuiInputBase-input": {
		color: "text.primary",
	},
	"& ~ .MuiDataGrid-panel .MuiInputBase-input::placeholder": {
		color: "text.secondary",
		opacity: 1,
	},
	"& ~ .MuiDataGrid-panel *::-webkit-scrollbar": { width: 8 },
	"& ~ .MuiDataGrid-panel *::-webkit-scrollbar-track": {
		background: "transparent",
	},
	"& ~ .MuiDataGrid-panel *::-webkit-scrollbar-thumb": {
		borderRadius: 999,
		background: (t: Theme) =>
			t.palette.mode === "dark"
				? alpha(t.palette.common.white, 0.22)
				: alpha(t.palette.common.black, 0.18),
	},
	"& ~ .MuiDataGrid-panel *::-webkit-scrollbar-thumb:hover": {
		background: (t: Theme) =>
			t.palette.mode === "dark"
				? alpha(t.palette.common.white, 0.32)
				: alpha(t.palette.common.black, 0.26),
	},
};

export const CustomToolbarV2: React.FC<Props> = ({
	rows,
	columns,
	fileName = "export",
	pdfMeta,
	quickFilterProps,
	hideQuickFilter = false,
	showPdfOptions = true,
	hidden = false,
}) => {
	const translation: Language | null = useContext(TranslationContext);
	return (
		<Toolbar
			sx={{
				display: hidden ? "none" : "flex",
				alignItems: "center",
				gap: 1,
				px: 1,
				py: 0.75,
				justifyContent: "flex-start",
				"--DataGrid-toolbarHeight": "46px",
				borderBottom: "1px solid",
				borderColor: "divider",
				bgcolor: "background.paper",
				...panelSx,
			}}
		>
			{!hideQuickFilter && (
				<GridToolbarQuickFilter
					quickFilterParser={(v) => v.split(/\s+/).filter(Boolean)}
					placeholder="Search this table..."
					{...quickFilterProps}
					InputProps={{
						startAdornment: (
							<InputAdornment position="start" sx={{ color: "text.secondary" }}>
								<SearchRoundedIcon fontSize="small" />
							</InputAdornment>
						),
					}}
					sx={{
						minWidth: 240,
						mr: "auto",
						"& .MuiOutlinedInput-root": {
							height: 36,
							borderRadius: 2.5,
							pr: 0.75,
							bgcolor: "background.paper",
							transition:
								"box-shadow 160ms ease, background-color 160ms ease, border-color 160ms ease",
							"& .MuiOutlinedInput-notchedOutline": {
								borderColor: "divider",
							},
							"&:hover .MuiOutlinedInput-notchedOutline": {
								borderColor: "text.disabled",
							},
							"&.Mui-focused": {
								boxShadow: (t: Theme) =>
									`0 0 0 3px ${alpha(t.palette.primary.main, t.palette.mode === "dark" ? 0.24 : 0.16)}`,
							},
						},
						"& .MuiOutlinedInput-input": {
							pl: 0.5,
							fontSize: 14,
							fontWeight: 500,
							color: "text.primary",
						},
					}}
				/>
			)}

			<Box sx={{ flexGrow: 1 }} />

			<ToolbarButton
				render={(props) => (
					<Tooltip title={GetLanguageValueAsString(translation, "Auto.Shared.DataGrid.CustomToolBarV2.L221C21.Columns", "Columns")} placement="bottom">
						<span>
							<ColumnsPanelTrigger
								{...props}
								render={
									<IconButton size="small" sx={getIconBtnSx()}>
										<ViewWeekRoundedIcon fontSize="small" />
									</IconButton>
								}
							/>
						</span>
					</Tooltip>
				)}
				aria-label={GetLanguageValueAsString(translation, "Auto.Shared.DataGrid.CustomToolBarV2.L234C16.Columns", "Columns")}
			/>

			<ToolbarButton
				render={(props) => (
					<Tooltip title={GetLanguageValueAsString(translation, "Auto.Shared.DataGrid.CustomToolBarV2.L239C21.Filter", "Filter")} placement="bottom">
						<span>
							<FilterPanelTrigger
								{...props}
								render={
									<IconButton size="small" sx={getIconBtnSx()}>
										<FilterAltRoundedIcon fontSize="small" />
									</IconButton>
								}
							/>
						</span>
					</Tooltip>
				)}
				aria-label={GetLanguageValueAsString(translation, "Auto.Shared.DataGrid.CustomToolBarV2.L252C16.Filter", "Filter")}
			/>

			<CustomExportButton
				rows={rows}
				columns={columns}
				fileName={fileName}
				pdfMeta={pdfMeta}
				showPdfOptions={showPdfOptions}
			/>
		</Toolbar>
	);
};
